"use client"
import { useMemo } from "react"
import { motion } from "framer-motion"
import { allCourses } from "@/pages/courses/data/courses"
import { CourseCard } from "@/components/course-card"

// Category descriptions
const categoryInfo = {
  "Quran": {
    title: "Quran Studies",
    description: "Master Quranic recitation, memorization, and understanding with certified teachers."
  },
  "Islamic Studies": {
    title: "Islamic Studies",
    description: "Build a strong foundation in Islamic knowledge, from Aqeedah to Fiqh and Seerah."
  },
  "Arabic": {
    title: "Arabic Language",
    description: "Learn Arabic for Quranic understanding, conversation, or academic purposes."
  }
}

export function CoursesCatalog() {
  // Group courses by category
  const coursesByCategory = useMemo(() => {
    const categories = ["Quran", "Islamic Studies", "Arabic"]
    return categories.map(cat => ({
      category: cat,
      info: categoryInfo[cat as keyof typeof categoryInfo],
      courses: allCourses
        .filter(c => c.category === cat)
        .sort((a, b) => {
          // Sort: Popular first, then by students
          const aPopular = a.badges?.includes("popular") ? 1 : 0
          const bPopular = b.badges?.includes("popular") ? 1 : 0
          if (bPopular !== aPopular) return bPopular - aPopular
          return parseInt(b.students) - parseInt(a.students)
        })
    }))
  }, [])

  return (
    <div className="bg-[#FCF8F1]">

      {/* Courses by Category */}
      <section className="py-10 sm:py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
          {coursesByCategory.map((categoryGroup, catIndex) => (
            <div key={categoryGroup.category}>
              {/* Category Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mb-10"
              >
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
                  {categoryGroup.info.title}
                </h2>
                <p className="text-lg text-gray-600 max-w-3xl">
                  {categoryGroup.info.description}
                </p>
              </motion.div>

              {/* Courses Grid */}
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {categoryGroup.courses.map((course, index) => (
                  <CourseCard key={course.id} course={course} index={index} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
